<template>
  <validation-observer ref="form" tag="div">
    <v-card color="grey lighten-1" class="pt-6" flat rounded="xl">
      <v-container class="px-6">
        <v-row>
          <v-col>
            <v-menu
              v-model="modalDeliveryDate"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="data.deliverydate | moment('DD MMMM YYYY, dddd')"
                  label="Teslimat Tarihi"
                  filled
                  rounded
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="data.deliverydate"
                :weekday-format="weekdayFormat"
                :show-current="false"
                :first-day-of-week="1"
                :min="minDeliveryDate"
                :max="maxDeliveryDate"
                @input="modalDeliveryDate = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-autocomplete
              v-model="data.deliveryhour"
              filled
              rounded
              label="Teslimat Saatı"
              :items="['10:00-17:00', '18:00-20:00']"
              autocomplete="new-password"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="Ödeme Şekli"
              rules="required"
            >
              <v-autocomplete
                v-model="data.paymenttype"
                :items="callCenterStore.paymentMethods"
                label="Ödeme Şekli"
                eager
                filled
                rounded
                auto-select-first
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="Gönderecek Mağaza"
              rules="required"
            >
              <v-autocomplete
                v-model="data.customer"
                label="Gönderecek Mağaza"
                filled
                rounded
                return-object
                :items="selectCustomerItems"
                :loading="selectCustomerLoading"
                :hint="selectCustomerErrorMsg"
                :persistent-hint="selectCustomerError"
                auto-select-first
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="İl"
              rules="required"
            >
              <v-autocomplete
                v-model="data.city"
                :items="CITY_STATE"
                item-text="il"
                return-object
                label="İl"
                eager
                filled
                rounded
                auto-select-first
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="İlçe"
              rules="required"
            >
              <v-autocomplete
                v-model="data.state"
                label="İlçe"
                filled
                rounded
                :items="data.city.ilceleri"
                auto-select-first
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="Adres"
              rules="required|min:10"
            >
              <v-textarea
                v-model="data.address"
                label="Adres"
                rows="3"
                filled
                rounded
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              tag="div"
              mode="eager"
              name="Sipariş Notu"
            >
              <v-textarea
                v-model="data.notes"
                label="Sipariş Notu"
                rows="3"
                filled
                rounded
                autocomplete="new-password"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-checkbox
              v-model="data.needinvoice"
              color="secondary"
              hide-details
              label="Fatura/Fiş gönderilmesini istemiyorum."
            />
          </v-col>
        </v-row>
        <v-row dense class="pb-3">
          <v-col>
            <v-checkbox
              v-model="data.isdifferentinvoiceaddr"
              @change="
                callCenterStore.isInvoiceShow = !callCenterStore.isInvoiceShow
              "
              color="secondary"
              label="Fatura adresi farklı."
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <v-container class="px-6">
      <v-row align="center" class="my-9">
        <v-col md="6">
          <v-btn
            class="text-capitalize"
            plain
            x-large
            block
            rounded
            @click="callCenterStore.decrease()"
            >Geri Git
          </v-btn>
        </v-col>
        <v-col md="6">
          <v-btn
            color="secondary"
            class="text-capitalize"
            depressed
            rounded
            x-large
            block
            @click="continueStep()"
          >
            Devam Et
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </validation-observer>
</template>

<script>
import { onMounted, ref } from "@vue/composition-api";

// Utils
import {
  postData,
  addDays,
  formatDate,
  weekdayFormat,
  hoursAndMinutes,
  hoursAndMinutesSelect,
  CITY_STATE,
} from "@utils";
import { useRouter } from "@core/utils";

// Store
import { useCallCenterStore } from "@/stores/callCenter";

// Icon
import { mdiCalendar, mdiClock } from "@mdi/js";

export default {
  setup() {
    // Store
    const callCenterStore = useCallCenterStore();

    const { route } = useRouter();

    const form = ref(null);

    const data = ref({
      deliverydate: formatDate(addDays(0)),
      deliveryhour: "",
      paymenttype: "",
      customer: "",
      city: "",
      state: "",
      address: "",
      notes: "",
      needinvoice: false,
      isdifferentinvoiceaddr: false,
    });

    // DeliveryDate
    const modalDeliveryDate = ref(false);
    const minDeliveryDate = ref(formatDate(addDays(0)));
    const maxDeliveryDate = ref(formatDate(addDays(30)));

    // Customer
    const selectCustomerError = ref(false);
    const selectCustomerLoading = ref(true);
    const selectCustomerErrorMsg = ref("");
    const selectCustomerItems = ref([]);

    const customerFetch = () => {
      selectCustomerError.value = false;
      selectCustomerLoading.value = true;
      postData({
        method: "callCenterBranchList",
      })
        .then((response) => {
          // API dönüşündeki hatalara bakıyoruz
          if (response.error === 1) {
            throw new Error(response.msg);
          }
          selectCustomerItems.value = response.detail.map((item) => {
            return {
              text: item.officename,
              value: item.customer,
            };
          });
        })
        .catch((e) => {
          // API hataya girdi
          selectCustomerItems.value = [];
          selectCustomerError.value = true;
          selectCustomerErrorMsg.value = e.message;
        })
        .finally(() => {
          // API işlemi bittikten sonra
          selectCustomerLoading.value = false;
        });
    };

    const continueStep = async () => {
      const isValid = await form.value.validate();

      if (!isValid) {
        return;
      }
      callCenterStore.data.delivery = data.value;
      callCenterStore.increment();
    };

    onMounted(async () => {
      await customerFetch();
      if (route.value.params.item) {
        data.value = callCenterStore.data.delivery;
        data.value.city = CITY_STATE.find(
          (item) => item.il === data.value.city
        );
      }
    });

    return {
      minDeliveryDate,
      maxDeliveryDate,
      selectCustomerItems,
      selectCustomerError,
      selectCustomerErrorMsg,
      selectCustomerLoading,
      weekdayFormat,
      modalDeliveryDate,
      callCenterStore,
      continueStep,
      form,
      data,
      CITY_STATE,
      hoursAndMinutes,
      icons: {
        mdiCalendar,
        mdiClock,
      },
    };
  },
};
</script>

<style></style>
